import * as React from "react";
import { useDrag, useDrop, DropTargetMonitor } from "react-dnd-latest";
import { $, merge } from "glamor";
import debounce from "lodash/debounce";

interface IProps {
    index: number;
    id: string;
    onMove: (dragIndex: number, hoverIndex: number) => void;
    children: (props: { ref: React.MutableRefObject<HTMLDivElement>; isDragging: boolean }) => React.ReactNode;
}

interface IDragItem {
    index: number;
    id: string;
    type: string;
}

const styles = merge(
    $(" .dimensionCheckBox", {
        display: "flex",
        alignItems: "center",
        width: "100%",
    }),
    $(" .dimensionCheckBox .MuiListItemIcon-root", {
        minWidth: "auto !important",
        display: "flex",
        alignItems: "center",
    }),
    $(" .dragHandler", {
        padding: "2px",
        cursor: "move",
        minWidth: "unset",
    })
);

export const ListDraggableItem: React.FunctionComponent<IProps> = ({ id, children, index, onMove }) => {
    const ref = React.useRef<HTMLDivElement>(null);

    const [{ handlerId }, drop] = useDrop<IDragItem, void, { handlerId: any }>({
        accept: "card",
        collect(monitor) {
            return {
                handlerId: monitor.getHandlerId(),
            };
        },
        hover: debounce((item: IDragItem, monitor: DropTargetMonitor) => {
            if (!ref.current) {
                return;
            }
            const dragIndex = item.index;
            const hoverIndex = index;

            // Don't replace items with themselves
            if (dragIndex === hoverIndex) {
                return;
            }

            // Determine rectangle on screen
            const hoverBoundingRect = ref.current?.getBoundingClientRect();

            // Get vertical middle
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;

            // Determine mouse position
            // Default object added as a guard clause if method returns null
            const clientOffset = monitor.getClientOffset() || { x: 0, y: 0 };

            // Get pixels to the top
            const hoverClientY = clientOffset?.y - hoverBoundingRect.top;

            // Only perform the move when the mouse has crossed half of the items height
            // When dragging downwards, only move when the cursor is below 50%
            // When dragging upwards, only move when the cursor is above 50%

            // Dragging downwards
            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }

            // Dragging upwards
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }

            // Time to actually perform the action
            onMove(dragIndex, hoverIndex);

            // Note: we're mutating the monitor item here!
            // Generally it's better to avoid mutations,
            // but it's good here for the sake of performance
            // to avoid expensive index searches.
            item.index = hoverIndex;
        }, 50),
    });

    const [{ isDragging, opacity }, drag] = useDrag({
        item: { type: "card", id, index },
        isDragging: (monitor) => monitor.getItem()?.id === id,
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
            opacity: monitor.isDragging() ? 0 : 1,
        }),
    });

    drag(drop(ref));

    return (
        <div ref={ref} {...styles} style={{ opacity }} data-handler-id={handlerId} key={id}>
            {children({ ref, isDragging })}
        </div>
    );
};
